import React from 'react'
import {
  useDropzone,
  DropzoneOptions,
  FileRejection,
  ErrorCode
} from 'react-dropzone'
import styled from 'styled-components'
import {formatByteSize} from '../../formatters'
import {Loader} from '../../loader'
import {Button} from '../buttons'
import {IconButton} from '../buttons/icon-button'
import {Icon} from '../icon/icon'

const Root = styled.div`
  cursor: pointer;
  outline: none;
`

const DropAdd = styled.div<{
  height?: string
}>`
  text-align: center;
  padding: 30px;
  border: 1px dashed ${({theme}) => theme.grey20};
  border-radius: ${({theme}) => theme.borderRadius};
  min-height: ${p => (p.height ? p.height : '400px')};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 400px;
  flex-direction: column;
`

const IconEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-bottom: 10px;
  svg {
    flex: none;
  }
`

const TitleEmpty = styled.div`
  font-size: ${p => p.theme.fontSizeL};
  line-height: ${p => p.theme.lineHeightL};
  margin-bottom: 10px;
`

const DescEmpty = styled.div`
  font-size: ${p => p.theme.fontSize};
  line-height: ${p => p.theme.lineHeightCompact};
  color: ${({theme}) => theme.grey50};
  margin-bottom: 20px;
`

const DropZone = styled.div<{
  height?: string
}>`
  text-align: center;
  padding: 30px;
  height: ${p => (p.height ? p.height : '400px')};
  border: 1px solid ${({theme}) => theme.blueLightest};
  border-radius: ${({theme}) => theme.borderRadius};
  background-color: ${({theme}) => theme.white};
  background-image: linear-gradient(
    ${p => p.theme.blueLightest},
    ${p => p.theme.blueLightest}
  );

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-width: 400px;
  position: relative;
  z-index: 2;
  color: ${({theme}) => theme.blue};
`

const TitleDrop = styled.div`
  font-size: ${p => p.theme.fontSizeL};
  line-height: ${p => p.theme.lineHeightL};
`

const DropAdded = styled.div<{
  height?: string
}>`
  height: ${p => (p.height ? p.height : '400px')};
  width: 400px;
  position: relative;
  display: flex;
  padding: 30px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
  button {
    position: absolute;
    top: 30px;
    right: 30px;
  }
`

export const FilePositioner = styled.div`
  position: relative;
`

export const FilePreview = styled.div<{
  height?: string
}>`
  width: 400px;
  height: ${p => (p.height ? p.height : '400px')};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${p => p.theme.borderRadius};
  overflow: hidden;
  pointer-events: none;
  background-image: linear-gradient(
    ${p => p.theme.grey05},
    ${p => p.theme.grey05}
  );
  background-color: ${p => p.theme.white};
  position: absolute;
  top: 0;
  left: 0;
  &:after {
    content: '';
    position: absolute;
    border-radius: ${p => p.theme.borderRadius};
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: ${p => (p.height ? p.height : '400px')};
    border: 1px solid ${p => p.theme.grey05};
  }
  &:hover {
    z-index: 0;
  }
`

export const Image = styled.img<{
  height?: string
}>`
  max-width: 400px;
  max-height: ${p => (p.height ? p.height : '400px')};
  width: auto;
  height: auto;
  object-fit: contain;
  background: transparent;
`

interface FileDropProps {
  onDrop: (files: File[]) => void
  title?: string
  desc?: string
  drop?: string
  success?: string
  isLoading?: boolean
  dropzoneProps: {maxSize: number} & Omit<DropzoneOptions, 'maxSize'> // ensure that max size is always supplied
  children?: React.ReactNode
  hasPreview?: boolean
  height?: string
  disabled?: boolean
}

export const FileDrop = ({
  onDrop,
  desc,
  title,
  drop,
  success,
  isLoading,
  dropzoneProps,
  children,
  hasPreview,
  height,
  disabled = false
}: FileDropProps) => {
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    multiple: false,
    ...(dropzoneProps ? dropzoneProps : {})
  })

  return (
    <>
      {/*
      * Dropzone gets a bit overzealous with its prop spread here, upsetting typescript around css types
      * " Type 'null' is not assignable to type 'string | CSSObject | FlattenInterpolation<ThemeProps<any>> | undefined " amongst other things
      //@ts-ignore */}
      <Root data-testid={'file-drop'} {...getRootProps()}>
        {/*
        //@ts-ignore */}
        <input data-testid="dropzone-input" {...getInputProps()} />
        {isLoading && <Loader />}
        {!!children && children}
        {isDragActive ? (
          <DropZone height={height}>
            <IconEmpty>
              <Icon name="upload" />
            </IconEmpty>
            <TitleDrop>{drop ? drop : 'Drop to upload'}</TitleDrop>
          </DropZone>
        ) : hasPreview ? (
          <DropAdded height={height}>
            {success && (
              <IconEmpty>
                <Icon name="file" />
              </IconEmpty>
            )}
            {success && <TitleEmpty>{success}</TitleEmpty>}

            <IconButton
              icon="file_add"
              size="large"
              appearance="bordered"
              variant="color"
            />
          </DropAdded>
        ) : (
          <DropAdd height={height}>
            <IconEmpty>
              <Icon name="file" />
            </IconEmpty>
            <TitleEmpty>{title ? title : 'No file selected'}</TitleEmpty>
            <DescEmpty>
              {desc ? desc : 'Select manually or drag and drop here'}
            </DescEmpty>
            <Button
              data-testid={'button-dropzone-select'}
              disabled={disabled}
              iconLeft="folder"
              appearance="primary"
            >
              Select file
            </Button>
          </DropAdd>
        )}
      </Root>
    </>
  )
}

export const onDropRejectedHandler =
  (errorHandler: (message: string) => void, maxUploadLimitBytes: number) =>
  (fileRejections: FileRejection[]) => {
    const errors: string[] = fileRejections.reduce<string[]>((acc, curr) => {
      return [...acc, ...curr.errors.map(e => e.code)]
    }, [])

    if (errors.some(e => e === ErrorCode.FileTooLarge)) {
      errorHandler(
        `Error uploading media, files must be less than ${formatByteSize(
          maxUploadLimitBytes
        )} in size`
      )
      return
    }

    // assume the other error is the multiple
    if (errors.some(e => e === ErrorCode.TooManyFiles)) {
      errorHandler(
        'Error uploading media, ensure only one png, jpeg, jpg, or gif is selected'
      )
    }

    errorHandler(
      `Error uploading media. ${fileRejections?.[0].errors?.[0].message}`
    )
  }
